import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Base HTTP service that abstracts HttpClient
 * Centralizes common HTTP operations and base URL configuration
 */
@Injectable({ providedIn: 'root' })
export class BaseHttpService {
  /**
   * HttpClient instance
   */
  private readonly http = inject(HttpClient);
  /**
   * Base API URL from environment
   */
  private readonly baseUrl = environment.creditLineApi;
  /**
   * Default HTTP headers
   */
  private readonly defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  /**
   * Perform GET request
   *
   * @param path - API path (will be concatenated with baseUrl)
   * @param params - Optional query parameters
   * @param headers - Optional custom headers
   * @returns Observable with response data
   */
  public get<T>(path: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const url = `${this.baseUrl}/${path}`;
    const requestHeaders = headers || this.defaultHeaders;

    return this.http.get<T>(url, { params, headers: requestHeaders });
  }

  /**
   * Perform POST request
   *
   * @param path - API path (will be concatenated with baseUrl)
   * @param body - Request body data
   * @param params - Optional query parameters
   * @param headers - Optional custom headers
   * @returns Observable with response data
   */
  public post<T>(path: string, body: any, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const url = `${this.baseUrl}/${path}`;
    const requestHeaders = headers || this.defaultHeaders;

    return this.http.post<T>(url, body, { params, headers: requestHeaders });
  }

  /**
   * Perform PUT request
   *
   * @param path - API path (will be concatenated with baseUrl)
   * @param body - Request body data
   * @param params - Optional query parameters
   * @param headers - Optional custom headers
   * @returns Observable with response data
   */
  public put<T>(path: string, body: any, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const url = `${this.baseUrl}/${path}`;
    const requestHeaders = headers || this.defaultHeaders;

    return this.http.put<T>(url, body, { params, headers: requestHeaders });
  }

  /**
   * Perform DELETE request
   *
   * @param path - API path (will be concatenated with baseUrl)
   * @param params - Optional query parameters
   * @param headers - Optional custom headers
   * @returns Observable with response data
   */
  public delete<T>(path: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const url = `${this.baseUrl}/${path}`;
    const requestHeaders = headers || this.defaultHeaders;

    return this.http.delete<T>(url, { params, headers: requestHeaders });
  }
}
